import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { categoriesActions } from 'reduxStore/categories/slice';
import { selectFieldTypes } from 'reduxStore/types/selectors';
import { Button, Col, Flex, Form, Input, Row, Select, Tooltip } from 'zui/Antd';

const AddCategoryFields = ({ categoryID, setAddField, setIsEditing }) => {
	// Hooks
	const [form] = Form.useForm();
	const dispatch = useDispatch();
	const {
		token: { marginMD, fontSize, colorTextDescription, paddingMD }
	} = theme.useToken();

	// Redux state
	const fieldTypes = useSelector(selectFieldTypes);
	const { loadingCreateCategoryField } = useSelector((state) => state.Categories);

	// Local state to manage fields being added
	const [fields, setFields] = useState([{ fieldID: undefined, title: '' }]);

	// Memoized field types for select options
	const isSubmitDisabled = useMemo(() => {
		return fields.some((field) => !field.fieldTypeID || !field.title?.trim());
	}, [fields]);

	// Handle adding a new field
	const handleAdd = () => {
		setFields([...fields, { fieldID: undefined, title: '', fieldTypeID: undefined }]);
	};

	// Handle field removal
	const handleRemove = (index) => {
		if (fields.length === 1) {
			handleCancelAdd();
			return;
		}
		const updated = [...fields];
		updated.splice(index, 1);
		setFields(updated);
	};

	// Handle field changes
	const handleFieldChange = (index, key, value) => {
		const updated = [...fields];
		updated[index][key] = value;
		setFields(updated);
	};

	// Cancel adding fields
	// Reset fields and close add field mode
	const handleCancelAdd = () => {
		setFields([{ fieldID: undefined, title: '', fieldTypeID: undefined }]);
		setAddField(false);
		setIsEditing(false);
	};

	// Create new category fields
	const handleSubmit = ({ fields }) => {
		const validFields = fields.filter((f) => f.title?.trim() && f.fieldTypeID);
		if (validFields.length) {
			dispatch(categoriesActions.createCategoryField({ categoryID, data: { fields: validFields } }));
		}
		handleCancelAdd();
	};

	return (
		<Form form={form} layout='vertical' onFinish={handleSubmit}>
			{fields.map((fieldItem, index) => (
				<Row key={index} gutter={[paddingMD, paddingMD]} style={{ marginBottom: marginMD }} align='middle'>
					<Col xs={24} md={12}>
						<Form.Item
							name={['fields', index, 'title']}
							rules={[{ required: true, message: 'Please enter a label' }]}
							style={{ marginBottom: 0 }}
						>
							<Input
								placeholder='Field Label'
								value={fieldItem.title}
								onChange={(e) => handleFieldChange(index, 'title', e.target.value)}
							/>
						</Form.Item>
					</Col>

					<Col xs={24} md={12}>
						<Flex justify='space-between' align='center'>
							<Form.Item
								name={['fields', index, 'fieldTypeID']}
								rules={[{ required: true, message: 'Please select field type' }]}
								style={{ marginBottom: 0, flex: 1 }}
							>
								<Select
									placeholder='Select Type'
									options={fieldTypes.map((type) => ({
										label: type.name,
										value: type.fieldTypeID
									}))}
									value={fieldItem.fieldTypeID}
									onChange={(value) => handleFieldChange(index, 'fieldTypeID', value)}
								/>
							</Form.Item>

							<Tooltip title='Remove Field'>
								<Button
									type='text'
									icon={<CloseOutlined style={{ fontSize: fontSize, color: colorTextDescription }} />}
									onClick={() => handleRemove(index)}
									disabled={loadingCreateCategoryField}
								/>
							</Tooltip>
						</Flex>
					</Col>
				</Row>
			))}

			<Form.Item>
				<Button type='dashed' onClick={handleAdd} block icon={<PlusOutlined />}>
					Add Field
				</Button>
			</Form.Item>

			<Flex justify='flex-end' align='center' style={{ marginTop: marginMD, marginBottom: marginMD }} gap={8}>
				<Button onClick={handleCancelAdd} disabled={loadingCreateCategoryField}>
					Cancel
				</Button>
				<Button
					type='primary'
					htmlType='submit'
					disabled={isSubmitDisabled || loadingCreateCategoryField}
					loading={loadingCreateCategoryField}
				>
					Add Fields
				</Button>
			</Flex>
		</Form>
	);
};

export default AddCategoryFields;
